import { useEffect, useState, type FormEvent } from "react";
import { HOME_REMINDER_PRESETS, type HomeShareInfo } from "@handvero/shared";
import { api, errorText } from "../api";
import { Message, Sheet } from "./ui";

/*
 * HandVero Zuhause: Erinnerungen für Wartung und Prüfungen sowie „Mein Handwerker“ (Ordner mit einem Betrieb teilen).
 * Die Erinnerung sagt nur, dass etwas ansteht. Arbeiten an Strom, Gas und Heizung gehören in Fachhände.
 */

const today = () => new Date().toISOString().slice(0, 10);
const day = (iso: string) => new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

export function ReminderSheet({ projectId, onClose }: { projectId?: string; onClose: () => void }) {
  const [preset, setPreset] = useState(HOME_REMINDER_PRESETS[0]!.id);
  const [title, setTitle] = useState(HOME_REMINDER_PRESETS[0]!.title);
  const [date, setDate] = useState(today());
  const [months, setMonths] = useState(HOME_REMINDER_PRESETS[0]!.everyMonths);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<number | null>(null);
  const current = HOME_REMINDER_PRESETS.find((p) => p.id === preset)!;

  function pick(id: string) {
    const p = HOME_REMINDER_PRESETS.find((x) => x.id === id)!;
    setPreset(id);
    setTitle(p.title);
    setMonths(p.everyMonths);
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await api.post<{ created: number }>("/api/home/reminders", { title, firstDate: date, everyMonths: months, projectId });
      setDone(res.created);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title="Erinnerung anlegen" onClose={onClose}>
      {done !== null ? (
        <div className="device">
          <p>
            Fertig. {done === 1 ? "Der Termin steht" : `Die nächsten ${done} Termine stehen`} im Kalender.
          </p>
          <button className="app-btn" onClick={onClose}>
            Schließen
          </button>
        </div>
      ) : (
        <form className="device" onSubmit={save}>
          <label className="field">
            <span>Was soll ich Sie erinnern?</span>
            <select value={preset} onChange={(e) => pick(e.target.value)}>
              {HOME_REMINDER_PRESETS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title || "Eigene Erinnerung"}
                </option>
              ))}
            </select>
          </label>
          <p className="muted-s">{current.hint}</p>
          <label className="field">
            <span>Name</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} required />
          </label>
          <label className="field">
            <span>Erster Termin</span>
            <input type="date" value={date} min={today()} onChange={(e) => setDate(e.target.value)} required />
          </label>
          <label className="field">
            <span>Wiederholen</span>
            <select value={months} onChange={(e) => setMonths(Number(e.target.value))}>
              <option value={0}>Nur einmal</option>
              <option value={6}>Alle 6 Monate</option>
              <option value={12}>Jedes Jahr</option>
              <option value={24}>Alle 2 Jahre</option>
              <option value={48}>Alle 4 Jahre</option>
            </select>
          </label>
          <Message>{error}</Message>
          <button className="app-btn" type="submit" disabled={busy}>
            {busy ? "Wird angelegt …" : "Erinnerung anlegen"}
          </button>
        </form>
      )}
    </Sheet>
  );
}

export function HandwerkerSheet({ folderId, folderName, onClose }: { folderId: string; folderName: string; onClose: () => void }) {
  const [shares, setShares] = useState<HomeShareInfo[]>([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [days, setDays] = useState(30);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const load = () => api.get<HomeShareInfo[]>("/api/home/shares").then((s) => setShares(s.filter((x) => x.folderId === folderId)), () => undefined);
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderId]);

  async function send(e: FormEvent) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/api/home/shares", { folderId, tradesperson: name, email, message: message || undefined, days });
      setSent(true);
      setName("");
      setEmail("");
      setMessage("");
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    await api.del(`/api/home/shares/${id}`).catch((err) => setError(errorText(err)));
    await load();
  }

  const active = shares.filter((s) => !s.revoked && new Date(s.expiresAt).getTime() > Date.now());
  return (
    <Sheet title="Mein Handwerker" onClose={onClose}>
      <form className="device" onSubmit={send}>
        <p>
          Der Betrieb sieht den Ordner „{folderName}“ mit allen Unterordnern, kann Dateien ansehen und herunterladen, aber nichts ändern. Er braucht kein Konto. Sie
          können den Link jederzeit zurückziehen.
        </p>
        <label className="field">
          <span>Betrieb oder Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required />
        </label>
        <label className="field">
          <span>E-Mail des Betriebs</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" required />
        </label>
        <label className="field">
          <span>Nachricht (freiwillig)</span>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={1000} rows={3} />
        </label>
        <label className="field">
          <span>Link gültig für</span>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={7}>7 Tage</option>
            <option value={30}>30 Tage</option>
            <option value={90}>90 Tage</option>
          </select>
        </label>
        <Message>{error}</Message>
        {sent && <p>Die E-Mail ist unterwegs.</p>}
        <button className="app-btn" type="submit" disabled={busy}>
          {busy ? "Wird verschickt …" : "Link per E-Mail schicken"}
        </button>
      </form>
      {active.length > 0 && (
        <div className="device" style={{ marginTop: 16 }}>
          <b>Aktuell geteilt</b>
          <ul className="acc-devices">
            {active.map((s) => (
              <li key={s.id}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <b>{s.tradesperson}</b>
                  <br />
                  <small>
                    bis {day(s.expiresAt)}
                    {s.lastOpenedAt ? ` · zuletzt geöffnet ${day(s.lastOpenedAt)}` : " · noch nicht geöffnet"}
                  </small>
                </span>
                <button type="button" className="app-btn small ghost" onClick={() => void revoke(s.id)}>
                  Zurückziehen
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Sheet>
  );
}
